import { CircularProgress } from '@material-ui/core';
import { Mic, MicOff, Stop, Close, Restore, Delete } from '@material-ui/icons';
import Webcam from 'react-webcam';
import styles from './RecordVideo.module.css';
import RecorderButton from './RecorderButton';
import Timer from 'shared_ui_components/VideoRecorder/Timer/Timer';
import RecordVideoLogic from './RecordVideo.logic';
import BounceLoaderOverlay from './BounceLoaderOverlay';
import ExitPageModal from './ExitPageModal/ExitPageModal';
import TimeSnackbar from '../Common/TimeSnackbar';
import VideoPlayer from '../Common/VideoPlayer';
import TenMinutesLeft from './TenMinutesLeft';
import GetReadyModal from 'features/roleplay/Components/GetReadyModal/GetReadyModal';

const isInteractiveRolePlay = (roleplayName = '') =>
  roleplayName?.toLowerCase?.() === 'objection_handling' ||
  roleplayName?.toLowerCase?.() === 'elevator_pitch';

const RecordVideo = ({
  onCancelRecord = () => null,
  onStartRecord = () => null,
  isPublic = false,
  isSelfAssign = false,
  aiProspectOption = false,
}) => {
  const {
    isAudioOn,
    isRecording,
    isLoading,
    time,
    previewUrl,
    webcamRef,
    canvasRef,
    toggleAudio,
    stopRecording,
    prevStartRecording,
    isLoadingSession,
    onRestart,
    onDelete,
    onReRecord,
    onLeavePage,
    toast,
    handleCloseToast,
    cameraError,
    handleCameraError,
    audioError,
    isContextModalShowed,
    onConfirmContextModal,
    rolePlayType,
    scenario,
    customer_context,
    showNewChanges,
  } = RecordVideoLogic({ onCancelRecord, onStartRecord, isPublic, aiProspectOption });

  return (
    <>
      <div className={styles.container}>
        <div className={styles.videoContainer} style={{ position: 'relative' }}>
          <TimeSnackbar
            open={toast.open}
            message={toast.message}
            type={toast.type}
            onClose={handleCloseToast}
          />
          {!isRecording && previewUrl ? (
            <VideoPlayer url={previewUrl} controls={!!previewUrl} muted={!previewUrl} />
          ) : (
            <div className={styles.videoWrapper}>
              {cameraError ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    borderRadius: '4px',
                    backgroundColor: '#e8ebed',
                    fontSize: '16px',
                    textAlign: 'center',
                  }}
                >
                  We couldn’t access your camera. Make sure your browser has permission to use it.
                  If the camera still doesn’t work, try resetting the permissions and selecting the
                  correct device
                </div>
              ) : (
                <>
                  <Webcam
                    ref={webcamRef}
                    className={styles.video}
                    audio={false}
                    videoConstraints={{
                      facingMode: 'user',
                    }}
                    screenshotFormat="image/webp"
                    onUserMediaError={handleCameraError}
                  />
                  <canvas ref={canvasRef} className={styles.overlayCanvas} />
                  <BounceLoaderOverlay visible={isLoading} />
                  <TenMinutesLeft time={time} />
                  <>
                    {showNewChanges &&
                      isContextModalShowed &&
                      isInteractiveRolePlay(rolePlayType) && (
                        <GetReadyModal
                          mode={isSelfAssign ? 'practice' : 'assesment'}
                          scenario={scenario}
                          time={
                            rolePlayType?.toLowerCase?.() === 'elevator_pitch'
                              ? '1 Minute'
                              : '5 Minutes'
                          }
                          contextDescription={`This scenario focuses on a ${customer_context.role} in the ${customer_context.industry} industry, dealing with a situation where ${customer_context.situation}.`}
                          tips={[
                            'Validate their internal process as a sign of diligence',
                            'Tie benefits directly to their cost containment challenges',
                            'Offer two follow-up meeting paths: case study vs pilot vs checklist',
                          ]}
                          isOpen={isContextModalShowed}
                          onClose={onConfirmContextModal}
                        />
                      )}
                  </>
                </>
              )}
            </div>
          )}
        </div>
        {audioError && (
          <div>
            {' '}
            Could not access your microphone. Please check your browser permissions and ensure your
            microphone is connected. Try resetting the permissions and selecting the correct device
          </div>
        )}

        <div className={styles.controls}>
          {!isRecording && previewUrl && (
            <button className={styles.resetButton} onClick={onReRecord}>
              <svg
                width="18"
                height="21"
                viewBox="0 0 18 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 21C7.75 21 6.57917 20.7625 5.4875 20.2875C4.39583 19.8125 3.44583 19.1708 2.6375 18.3625C1.82917 17.5542 1.1875 16.6042 0.7125 15.5125C0.2375 14.4208 0 13.25 0 12C0 10.75 0.2375 9.57917 0.7125 8.4875C1.1875 7.39583 1.82917 6.44583 2.6375 5.6375C3.44583 4.82917 4.39583 4.1875 5.4875 3.7125C6.57917 3.2375 7.75 3 9 3H9.15L7.6 1.45L9 0L13 4L9 8L7.6 6.55L9.15 5H9C7.05 5 5.39583 5.67917 4.0375 7.0375C2.67917 8.39583 2 10.05 2 12C2 13.95 2.67917 15.6042 4.0375 16.9625C5.39583 18.3208 7.05 19 9 19C10.95 19 12.6042 18.3208 13.9625 16.9625C15.3208 15.6042 16 13.95 16 12H18C18 13.25 17.7625 14.4208 17.2875 15.5125C16.8125 16.6042 16.1708 17.5542 15.3625 18.3625C14.5542 19.1708 13.6042 19.8125 12.5125 20.2875C11.4208 20.7625 10.25 21 9 21Z"
                  fill="#0494FD"
                />
              </svg>
              Re-record roleplay
            </button>
          )}
          {!isRecording && !previewUrl && !isLoading && (
            <RecorderButton withBorder tooltip="Cancel" onClick={onCancelRecord} Icon={Close} />
          )}

          {!previewUrl && !isLoading && (
            <RecorderButton
              withBorder
              tooltip={isAudioOn ? 'Mute' : 'Unmute'}
              onClick={toggleAudio}
              Icon={isAudioOn ? Mic : MicOff}
            />
          )}

          {!previewUrl &&
            !isLoading &&
            (!isRecording ? (
              <>
                {(!isContextModalShowed || !isInteractiveRolePlay(rolePlayType) || !showNewChanges) && (
                  <button
                    className={styles.startButton}
                    onClick={prevStartRecording}
                    disabled={!isAudioOn || isLoadingSession || cameraError}
                    style={{
                      cursor:
                        !isAudioOn || isLoadingSession || cameraError || audioError
                          ? 'default'
                          : 'pointer',
                    }}
                  >
                    {isLoadingSession ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Start Roleplay'
                    )}
                  </button>
                )}
              </>
            ) : (
              <>
                <RecorderButton
                  withBorder
                  onClick={() => stopRecording(false, false)}
                  tooltip="Stop"
                  Icon={Stop}
                />
                <Timer seconds={time} />
                <RecorderButton withBorder onClick={onRestart} tooltip="Restart" Icon={Restore} />
                <RecorderButton withBorder onClick={onDelete} tooltip="Delete" Icon={Delete} />
              </>
            ))}
        </div>
      </div>
      {isRecording && <ExitPageModal onLeavePage={onLeavePage} />}
    </>
  );
};

export default RecordVideo;
